import {OutpatientPanel, OutpatientSidebar} from "./components/OutpatientPanel";
import { ReferralTrendsPanel, ReferralSidebar } from "./components/ReferralTrendsPanel";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "./lib/api";
import { authApi } from "./lib/auth-api";
import { ymJp } from "./lib/format";
import { FilterBar } from "./components/FilterBar";
import { AlertsPanel } from "./components/AlertsPanel";
import { AiAnalysisPanel } from "./components/AiAnalysisPanel";
import { ContextBar } from "./components/ContextBar";
import { TabNav, tabLabel } from "./components/TabNav";
import { SurgeryPanel } from "./components/SurgeryPanel";
import { KbActPanel } from "./components/KbActPanel";
import { SurgeryRpPanel } from "./components/SurgeryRpPanel";
import { DischargePanel, DischargeSidebar } from "./components/DischargePanel";
import { RoutePanel } from "./components/RoutePanel";
import { PlPanel } from "./components/PlPanel";
import { DiseasePanel } from "./components/DiseasePanel";
import { ProfitPanel } from "./components/ProfitPanel";
import { PrototypePanel } from "./components/PrototypePanel";
import { InpatientBudgetComparePanel } from "./components/InpatientBudgetComparePanel";
import { CorrelationPanel } from "./components/CorrelationPanel";
import {
  InpatientFeeAnalysisPanel,
  InpatientFeeSidebar,
  type InpatientFeeView,
} from "./components/InpatientFeeAnalysisPanel";
import {
  ShortStay3AnalysisPanel,
  ShortStay3Sidebar,
} from "./components/ShortStay3AnalysisPanel";
import { RoomChargePanel } from "./components/RoomChargePanel";
import { PersonnelPanel } from "./components/PersonnelPanel";
import { Kyusou3PacePanel } from "./components/Kyusou3PacePanel";
import { AuthHeader } from "./components/AuthHeader";
import { ManualButton } from "./components/manual";
import { useFilters, BETA_TABS, setMetaDateMax, urlHasAll } from "./store";
import { IS_GP, IS_THETA_ROUTE } from "./lib/gpMode";
// 旧 入院実績 構成は 2026-05-26 に PrototypePanel ベースに置換 (KpiCards / TrendGrid / MetricMatrix /
// DeptMetricTable は PrototypePanel 内部から間接利用)。 KpiCards/TrendGrid/MetricMatrix は現在
// App.tsx からは未参照だが、 将来戻す場合に備えて component 自体は残置。

export function App() {
  const navigate = useNavigate();
  const { data: meta, isLoading, error } = useQuery({
    queryKey: ["meta"],
    queryFn: api.meta,
    staleTime: Infinity,
  });
  const { data: me } = useQuery({
    queryKey: ["auth", "me"],
    queryFn: authApi.me,
    retry: false,
    staleTime: 30_000,
  });
  const f = useFilters();
  const [sbOpen, setSbOpen] = useState(true);  // [サイドバー折りたたみ 2026-06-26]
  const [inpatientFeeView, setInpatientFeeView] =
    useState<InpatientFeeView>("hospital");
  // [正式版GP 2026-08-25] 正式画面は常に医師人件費控除後GPを要求する。
  // URL/表記と server の応答がズレた場合は赤帯で止める。
  const gpModeMismatch = meta != null && IS_GP !== (meta.gpDef === "jinken");
  const isThetaAlias = IS_THETA_ROUTE && meta?.gpDef === "jinken";
  const isOutpatient = f.tab === "外来予約";
  const isReferral = f.tab === "紹介・入院分析";
  const isIncome = f.tab === "収入速報";
  const isDischarge = f.tab === "退院先分岐";
  const isInpatientFeeTab = f.tab === "入院料分析";
  const isAiAnalysis = f.tab === "AI分析";
  // 個室料(2026-09-26 正式昇格): 期間はパネル内で選ぶので、全体フィルタのサイドバー・ContextBar は出さない
  const isRoomCharge = f.tab === "室料差額";
  // 急性期総合体制加算3(2026-09-26 正式昇格): 年度はデータ時点で決まり、全体フィルタを使わないので全幅
  const isKyusou3 = f.tab === "急性期総合体制加算3";
  const isPersonnel = f.tab === "職員数・人件費";
  const isFullWidth = isIncome || isAiAnalysis || isRoomCharge || isKyusou3 || isPersonnel;
  const isShortStay3Tab = f.tab === "短期滞在3分析";
  const isInpatientFeeAnalysis = isInpatientFeeTab || isShortStay3Tab;

  // タブ + 対象月で document.title を動的更新 → Chrome 「PDF として保存」 のデフォルトファイル名に反映
  useEffect(() => {
    const ym = isDischarge ? `${f.periodFrom.slice(0,7)}〜${f.periodTo.slice(0,7)}` : f.asOfMonth.slice(0, 7);
    document.title = `${tabLabel(f.tab)}_${ym}_病院実績`;
  }, [f.tab, f.asOfMonth, f.periodFrom, f.periodTo]);

  // 正式版で beta タブ (粗利分析等) が選択状態だと content が空白になるため 先頭タブへ戻す
  // (localStorage / URL に古い tab が残っているケースの保険。 ベータ版は /beta で見る)
  useEffect(() => {
    if (BETA_TABS.includes(f.tab)) f.setTab("入院実績(前年度・予算比較)");
  }, [f.tab]);  // eslint-disable-line react-hooks/exhaustive-deps

  // [初期表示 2026-07-22] meta ロード後に最新確定月 (dateMax) を store へ登録し、
  //   ?as= 未指定なら asOfMonth を最新確定月へ収束させる (暫定プレースホルダを上書き)。
  //   - setMetaDateMax: URL sync の「既定(=最新月)なら as= を出さない」判定に使う。
  //   - ?as= が URL にあれば尊重 (過去月を deep link/ブックマークで固定できる)。
  //   これで asOfMonth 既定がデータ進行に自動追従し、古い月がブックマークに焼き付かない。
  useEffect(() => {
    if (!meta) return;
    setMetaDateMax(meta.dateMax);
    const urlAs = new URLSearchParams(window.location.search).get("as");
    if (!urlAs && !isDischarge && !isReferral && f.asOfMonth !== meta.dateMax) f.setAsOfMonth(meta.dateMax);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meta?.dateMax]);

  // 入院料分析は基準月を終点に過去24か月、短期滞在3は最新退院月を既定にする。
  useEffect(() => {
    if (!meta?.dateMax) return;
    if (urlHasAll("from", "to")) return;
    if (isInpatientFeeTab) {
      const latest = `${meta.dateMax.slice(0, 7)}-01`;
      const d = new Date(`${latest}T00:00:00`);
      d.setMonth(d.getMonth() - 23);
      f.setPeriodFrom(
        `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`,
      );
      f.setPeriodTo(latest);
    } else if (isShortStay3Tab) {
      const latest = `${meta.dateMax.slice(0, 7)}-01`;
      f.setPeriodFrom(latest);
      f.setPeriodTo(latest);
    }
  }, [
    isInpatientFeeAnalysis,
    isInpatientFeeTab,
    isShortStay3Tab,
    meta?.dateMax,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // iPhone で / にアクセスしたら /m へ自動 redirect (1 回のみ)
  // - iPad は除外 (画面広いので PC 版を見られる)
  // - escape hatch: ?pc=1 で PC 版強制
  useEffect(() => {
    if (!me?.user || isReferral || isOutpatient) return;   // 紹介実績の明示表示はモバイルでも維持
    const params = new URLSearchParams(window.location.search);
    if (params.get("pc") === "1") return;
    const ua = navigator.userAgent;
    const isIphone = /iPhone|iPod/.test(ua);   // iPad は除外
    if (isIphone) {
      navigate("/m", { replace: true });
    }
  }, [me?.user, navigate, isReferral, isOutpatient]);

  if (error) {
    return (
      <div className="p-8 max-w-3xl">
        <h1 className="text-xl font-semibold text-red-700 mb-2">起動エラー</h1>
        <pre className="bg-red-50 border border-red-200 rounded p-3 text-xs overflow-auto">
          {(error as Error).message}
        </pre>
        <p className="text-sm text-slate-500 mt-3">
          R 側で dashboard.duckdb が生成されているか確認してください:
        </p>
        <pre className="bg-slate-100 rounded p-2 text-xs mt-1">
          Rscript -e &quot;source(&apos;script/50_dashboard_build/build_dashboard_db.R&apos;)&quot;
        </pre>
      </div>
    );
  }

  if (isLoading || !meta) {
    return (
      <div className="p-8">
        <div className="animate-pulse text-slate-400">読み込み中…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* GP の表示名と server の定義が食い違う事故を画面上でも検知する。 */}
      {gpModeMismatch && (
        <div className="bg-red-600 text-white text-xs px-4 py-1 font-bold">
          ⚠ GP定義の不整合: 画面は正式GPなのに、データは
          {meta?.gpDef === "jinken" ? "正式GP" : "旧定義"} です。ページを再読み込みしてください。
        </div>
      )}
      {isThetaAlias && (
        <div data-theta-alias-banner className="no-print bg-slate-700 text-white text-xs px-4 py-1 flex items-center gap-2">
          <span>GP分析は正式版へ移行しました。この旧URLも同じ内容を表示しています。</span>
          <a href={`/${window.location.search}`} className="underline font-medium">正式URLを開く</a>
        </div>
      )}
      <header className="border-b border-slate-200 bg-white">
        {/* iPad / モバイル: header を 2 行に分けても OK にして、 タイトル と 右側情報 が重ならないようにする */}
        <div className="max-w-[1880px] mx-auto px-4 py-2 flex items-center justify-between gap-2 flex-wrap">
          <h1 className="text-base font-semibold whitespace-nowrap">
            病院実績ダッシュボード
            {/* built / schema 表示は 2026-05-22 にユーザ判断で非表示 (本番運用化) */}
          </h1>
          <div className="flex items-center gap-2 flex-wrap min-w-0">
            <span className={`text-xs text-slate-500${isFullWidth || isDischarge || isReferral || isOutpatient ? " hidden" : ""}`}>
              対象月 <b>{ymJp(f.asOfMonth)}</b>
              {f.tab === "手術"
                ? <> | 手術台帳 / {f.yotei}</>
                : <> | {f.granularity}分類 / {f.iryo} / {f.yotei}</>}
              {f.tab === "手術"
                ? f.surgeryFocusedDept && <> | <b className="text-amber-700">🎯 {f.surgeryFocusedDept}</b></>
                : f.focusedDept && <> | <b className="text-amber-700">🎯 {f.focusedDept}</b></>}
            </span>
            <span className="perf-pill">
              {meta.depts.length} 科 (小) / {meta.metrics.length} 指標
            </span>
            {/* [2026-08-19] 点数本は**独立サイト(埼玉石心会点数本)**へ移した(ユーザ指示)。
                以前は同一ホストの /knw/ を認証つきで中継していたが、点数本だけを使う人にも
                配れるよう、自前認証を持つ別サイトにした。ここは外部リンクにするだけ。
                ★中継の経路(server/index.ts の /knw/*)は同日に撤去した。 */}
            <a
              href="https://ss-tensubon.tail979c49.ts.net"
              target="_blank"
              rel="noopener"
              className="text-xs text-blue-700 hover:underline whitespace-nowrap"
              title="埼玉石心会点数本(告示・通知・疑義解釈を区分番号で。OCRを経由しない原文テキスト)"
            >
              📕 埼玉石心会点数本
            </a>
            <AuthHeader />
          </div>
        </div>
      </header>

      <TabNav />

      {/* サイドバーは折りたたみ可 (☰ で再展開)。段階的に狭く: 180px → 200px → 240px */}
      <main className={[
        // w-full: flex-col 親の中で mx-auto (auto margin) だけだと flex item が
        // shrink-to-fit してタブごとにコンテンツ幅がばらつく (狭い表のタブ = main が縮む)。
        // width:100% を明示して常に max-w (1880px) いっぱいに広げ、全タブで枠幅を統一する。
        (isReferral || isOutpatient) ? "w-full px-3 sm:px-4 py-3 grid gap-3 flex-1" : "w-full max-w-[1880px] mx-auto px-3 sm:px-4 py-3 grid gap-3 flex-1",
        (isReferral || isOutpatient) ? sbOpen ? "grid-cols-[minmax(0,1fr)] md:grid-cols-[220px_minmax(0,1fr)]" : "grid-cols-[minmax(0,1fr)]" : isFullWidth ? "grid-cols-[minmax(0,1fr)]" : isInpatientFeeAnalysis
          ? "grid-cols-[180px_minmax(0,1fr)] md:grid-cols-[200px_minmax(0,1fr)] xl:grid-cols-[240px_minmax(0,1fr)]"
          : sbOpen
          ? "grid-cols-[180px_minmax(0,1fr)] md:grid-cols-[200px_minmax(0,1fr)] xl:grid-cols-[240px_minmax(0,1fr)]"
          : "grid-cols-[minmax(0,1fr)]",
      ].join(" ")}>
        {isFullWidth ? null : isInpatientFeeTab ? (
          <InpatientFeeSidebar
            dateMin={meta.dateMin}
            dateMax={meta.dateMax}
            view={inpatientFeeView}
            onViewChange={setInpatientFeeView}
          />
        ) : isShortStay3Tab ? (
          <ShortStay3Sidebar dateMax={meta.dateMax} />
        ) : isDischarge ? (
          <DischargeSidebar />
        ) : sbOpen ? (
          isOutpatient ? <OutpatientSidebar /> : isReferral ? <ReferralSidebar meta={meta} onCollapse={() => setSbOpen(false)} /> : <FilterBar meta={meta} onCollapse={() => setSbOpen(false)} />
        ) : (
          <button
            onClick={() => setSbOpen(true)}
            className="no-print fixed left-1 top-24 z-30 card px-1.5 py-2 text-sm text-slate-600 hover:bg-slate-100 shadow"
            title="フィルタを開く"
          >☰</button>
        )}
        <div className="space-y-3 min-w-0">
          {!isInpatientFeeAnalysis && !isFullWidth && !isDischarge && !isReferral && !isOutpatient && (
            <ContextBar metrics={meta.metrics} builtAt={meta.builtAt} />
          )}
          {/* 各タブの「見方」マニュアルドロワー起動ボタン (未整備タブでは自動非表示・no-print) */}
          {!isInpatientFeeAnalysis && !isFullWidth && !isDischarge && !isReferral && !isOutpatient && (
            <div className="no-print flex justify-end -mt-1">
              <ManualButton tabKey={f.tab} />
            </div>
          )}

          {isOutpatient && <OutpatientPanel />}
          {isReferral && <ReferralTrendsPanel />}
          {isDischarge && <DischargePanel />}
          {isAiAnalysis && <AiAnalysisPanel />}
          {f.tab === "入院実績(前年度・予算比較)" && (
            // 2026-07-10 βから正式昇格 (先頭タブ・デフォルト)
            <InpatientBudgetComparePanel metrics={meta.metrics} builtAt={meta.builtAt} dateMax={meta.dateMax} />
          )}

          {f.tab === "要確認診療科" && (
            <>
              <AlertsPanel />
              {/* 2026-05-26: 下部を 新 入院実績 (PrototypePanel) に置換 */}
              <PrototypePanel metrics={meta.metrics} builtAt={meta.builtAt} />
            </>
          )}

          {/* 着地見込みタブは一旦非表示 (TabNav からも除外、 ForecastPanel/import は残置) */}

          {f.tab === "入院経路" && (
            <RoutePanel />
          )}

          {f.tab === "手術" && (
            <SurgeryPanel />
          )}

          {/* [2026-07-18 正式昇格] 手術売上 (会計RPベース・科別サマリー+主たる手術別) */}
          {f.tab === "手術売上" && (
            <SurgeryRpPanel metrics={meta.metrics} builtAt={meta.builtAt} />
          )}

          {/* [2026-07-18 正式昇格] 旧「行為別」(KbRevenuePanel) は非表示・後継の行為別売上のみ */}
          {f.tab === "行為別売上" && (
            // [2026-09-07] 年度枠 (dateMax の年度) と 開始月/終了月 を 入院実績 と同じ規則で使う
            <KbActPanel dateMax={meta.dateMax} metrics={meta.metrics} builtAt={meta.builtAt} />
          )}

          {f.tab === "入院料分析" && (
            <InpatientFeeAnalysisPanel view={inpatientFeeView} />
          )}

          {f.tab === "短期滞在3分析" && <ShortStay3AnalysisPanel />}

          {/* [2026-09-26 正式昇格] 個室料 (内部キー 室料差額) */}
          {isRoomCharge && <RoomChargePanel />}

          {/* [2026-09-26 正式昇格] 急性期総合体制加算3 (手術等の集積性の年度ペース) */}
          {isKyusou3 && <Kyusou3PacePanel />}
          {isPersonnel && <PersonnelPanel />}

          {isIncome && <iframe title="収入速報" src="/api/income-sokuhou/index.html" className="w-full border-0" style={{height: "calc(100vh - 150px)", minHeight: 850}} />}

          {f.tab === "相関分析" && (
            <CorrelationPanel metrics={meta.metrics} dateMax={meta.dateMax} />
          )}

          {f.tab === "経営PL" && (
            <PlPanel asOfMonth={f.asOfMonth} />
          )}

          {/* [2026-07-18 正式昇格] DPC分析・粗利分析(医師別なし・表示名「粗利分析」) */}
          {f.tab === "DPC分析" && (
            <DiseasePanel />
          )}

          {f.tab === "粗利分析(医師別なし)" && (
            <ProfitPanel showDoctor={false} />
          )}

        </div>
      </main>

      <footer className="text-xs text-slate-400 text-center py-4">
        Vite + React + Hono + DuckDB native / 経営企画部 / verify 283/0 pass (data correctness, 2026-05-20)
      </footer>
    </div>
  );
}
